import moment from 'moment';
import { defineStore } from 'pinia';
import { ref, computed, watch, readonly } from 'vue';

import { type SuccessResponse } from '@bv/api';
import {
  userApi,
  type ServiceUniqueName,
  type UserDetails,
  type ServiceDetails,
  type Workspace,
  type CallCenterDetails,
  type NewBusinessChatService,
  type ExtensionDetails,
  BaseServiceDetails,
  CloudLinesDetails,
} from '@bv/api/user';
import { type Config as NormalizationConfig, normalizerApi } from '@bv/api/voip/calls/normalizer';
import { useHostname } from '@bv/composables';
import { canAccessService } from '@bv/domain/billing';
import { useAuthStore, useBrandStore, useSipStore } from '@bv/stores';
import { ensureArray, sip as sipClass } from '@bv/utils';
import impersonateWidget from '@bv/utils/common/impersonate/widget';
import { goToConsole } from '@bv/utils/url/console/go-to';

/**
 * User store
 *
 * Manages user-related state, including user details, workspaces, services, and permissions.
 * Handles fetching and switching of the user's active context.
 */
export const useUserStore = defineStore('bvUser', () => {
  const sipStore = useSipStore();

  const guardServiceAccess = (service?: BaseServiceDetails): boolean => {
    if (!canAccessService(service?.status)) {
      goToConsole();
      return false;
    }
    return true;
  };

  /**
   * Current authenticated user's details
   */
  const userDetails = ref<UserDetails | null>(null);

  /**
   * Active promise for fetching user details to prevent duplicate requests
   * @private
   */
  const fetchPromise = ref<Promise<UserDetails> | null>(null);

  /**
   * Active promises for fetching service details, keyed by service unique name
   * @private
   */
  const fetchServicePromise = ref<Partial<Record<ServiceUniqueName, Promise<ServiceDetails> | null>>>({});

  /**
   * Active promise for fetching user details to prevent duplicate requests
   * @private
   */
  const switchWorkspacePromise = ref<Promise<SuccessResponse> | null>(null);

  /**
   * Active promise for fetching extension details to prevent duplicate requests
   * @private
   */
  const fetchExtensionDetailsPromise = ref<Promise<ExtensionDetails> | null>(null);

  /**
   * Indicates whether user details have been fetched
   */
  const isFetched = ref<boolean>(false);

  /**
   * Indicates whether user details are currently being fetched
   */
  const isFetching = computed<boolean>(
    () => !isFetched.value || !useBrandStore().config || !!fetchPromise.value || !!switchWorkspacePromise.value,
  );

  /**
   * Current user's extension details for call center functionality
   */
  const extensionDetails = ref<ExtensionDetails | null>(null);

  /**
   * Cached service details, keyed by service unique name
   */
  const services = ref<Partial<Record<ServiceUniqueName, ServiceDetails>>>({});

  /**
   * User's currently active workspace
   */
  const currentWorkspace = ref<Workspace | undefined>(undefined);

  /**
   * User's currently active service
   */
  const currentService = ref<ServiceDetails | undefined>(undefined);

  /**
   * Current authenticated user's permissions
   */
  const permissions = computed<string[]>(() => {
    const { isSuperAdmin } = useHostname();
    if (isSuperAdmin) {
      return userDetails.value?.role.permissions || [];
    } else if (currentService.value) {
      return services.value[currentService.value.unique_name]?.permissions || [];
    }
    return [];
  });

  /**
   * Indicates whether the current session is an impersonation session
   */
  const isImpersonateUser = ref<boolean>(false);

  /**
   * Phone number normalization configuration
   * @private
   */
  const _normalizerConfig = ref<NormalizationConfig | undefined>(undefined);

  /**
   * Call center service details from the services cache
   */
  const currentServiceDetails = computed<BaseServiceDetails | undefined>(() =>
    currentService.value ? (services.value[currentService.value.unique_name] as BaseServiceDetails) : undefined,
  );

  /**
   * Call center service details from the services cache
   */
  const callCenterService = computed<CallCenterDetails | undefined>(
    () => services.value['call-center'] as CallCenterDetails,
  );

  /**
   * New business chat service details from the services cache
   */
  const newChatService = computed<NewBusinessChatService | undefined>(
    () => services.value['new-chat'] as NewBusinessChatService,
  );

  /**
   * Cloud lines service details from the services cache
   */
  const cloudLinesService = computed<CloudLinesDetails | undefined>(
    () => services.value['cloud-lines'] as CloudLinesDetails,
  );

  /**
   * Checks if the current user is an owner of the active workspace or call center service
   */
  const isOwner = computed<boolean>(() => {
    return !!(currentWorkspace.value?.is_owner || callCenterService.value?.role === 'owner');
  });

  /**
   * Returns the call center settings
   */
  const callCenterSettings = computed(() => {
    return callCenterService.value?.settings;
  });

  /**
   * Phone number normalization configuration with fallback to default values
   */
  const normalizerConfig = computed<NormalizationConfig>(
    () =>
      (_normalizerConfig.value || {
        country_configs: {},
        fallback_country: '',
      }) as NormalizationConfig,
  );

  /**
   * Fetches and initializes the authenticated user's details from the API.
   *
   * This method includes caching: if details are already present and `forceUpdate` is false,
   * it returns the existing data. Otherwise, it fetches fresh data.
   *
   * After fetching, it performs several initialization tasks:
   * - Sets the user's recent workspace and service.
   * - Synchronizes the active service with environment variables if necessary.
   * - Fetches specific service details (e.g., 'call-center').
   * - Initializes the SIP connection based on the `sip` parameter.
   *
   * @param {object} [options={}] - Optional configuration for the operation.
   * @param {boolean} [options.forceUpdate=false] - If true, bypasses the cache and forces a refetch from the API.
   * @returns {Promise<UserDetails>} A promise that resolves with the user's details object.
   * @throws {Error} Throws an error if the API call fails.
   */
  const getUserDetails = async ({
    forceUpdate = false,
  }: {
    forceUpdate?: boolean;
  } = {}): Promise<UserDetails> => {
    // Return existing promise if fetch is already in progress
    if (fetchPromise.value) {
      return fetchPromise.value;
    }

    if (!forceUpdate && userDetails.value) {
      isFetched.value = true;
      return new Promise((resolve) => {
        resolve(userDetails.value!);
      });
    }

    await _resetServices();

    isFetched.value = false;

    fetchPromise.value = userApi
      .details()
      .then(async ({ data }) => {
        userDetails.value = data;
        isFetched.value = true;
        const { isSuperAdmin } = useHostname();

        if (isSuperAdmin) {
          if (userDetails.value.type !== 'employee') {
            goToConsole();
          }
        } else {
          _startImpersonationSession();

          await _setCurrentWorkSpaceAndService();

          if (!import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME) {
            await getAllServices();
          }

          if (sipStore.options) {
            await getService('call-center');
          } else {
            // Without this line impersonation widget does not display.
            getService();
          }
        }

        return data;
      })
      .finally(() => {
        fetchPromise.value = null;
      })
      .catch(() => goToConsole()) as Promise<UserDetails>;
    return fetchPromise.value;
  };

  /**
   * Fetches details for all services in the current workspace
   * Services are fetched in parallel and cached in the services object
   *
   * @returns {Promise<void>}
   */
  const getAllServices = async (): Promise<void> => {
    const servicePromises = (currentWorkspace.value?.services || []).map((service) => getService(service.unique_name));
    await Promise.all(servicePromises);
  };

  /**
   * Async permission check that awaits `getUserDetails()` before evaluating.
   *
   * @deprecated Prefer `await getUserDetails()` then `usePermission().can(permission).value`.
   * Route guards should use `evaluateRouteMetaAccessAsync`, which already loads user details
   * and checks via `usePermission().can`.
   *
   * @param {string | string[]} permission - A single permission string or an array of permissions to check.
   * @returns {Promise<boolean>} A promise resolving to true if the user has all permissions, otherwise false.
   * @see usePermission().can — sync/reactive; does not fetch.
   */
  const hasPermissions = async (permission: string | string[]): Promise<boolean> => {
    const checkPermissions = ensureArray(permission);

    return getUserDetails().then(() => {
      const validPermissions = checkPermissions.filter((value) => permissions.value.includes(value));

      return validPermissions.length === checkPermissions.length;
    });
  };

  /**
   * Fetches details for a specific service within a workspace and caches it.
   *
   * @param {ServiceDetails} [service] - The ID of the service. Defaults to the current one.
   * @returns {Promise<ServiceDetails>} A promise that resolves with the service details.
   * @throws {Error} Throws an error if the API call fails
   */
  const getWorkspaceService = async (service?: ServiceDetails): Promise<ServiceDetails> => {
    return userApi
      .workspaceService(currentWorkspace.value?.id, service?.id)
      .then(async ({ data }) => {
        services.value[data.unique_name] = data;

        return data;
      })
      .catch((e) => {
        throw new Error(e);
      });
  };

  /**
   * Switches the user's active workspace and/or service and re-fetches user details.
   *
   * @param {Workspace} workspace - The target workspace.
   * @param {ServiceDetails | undefined} service - The target service.
   * @param {boolean} [skipRefetch=false] - When true, skips the `getUserDetails` re-fetch after the
   *   switch API call.
   * @returns {Promise<SuccessResponse>} The response from the switch API call.
   * @throws {Error} Throws an error if the API call fails
   */
  const switchWorkspaceService = async (
    workspace: Workspace,
    service?: ServiceDetails,
    skipRefetch: boolean = false,
  ): Promise<SuccessResponse> => {
    // Return existing promise if fetch is already in progress
    if (switchWorkspacePromise.value) {
      return switchWorkspacePromise.value;
    }

    const serviceId = service?.id || workspace.services[0]?.id;

    switchWorkspacePromise.value = userApi
      .switchWorkspaceService(workspace.id, serviceId)
      .then(async (response) => {
        currentWorkspace.value = workspace;

        if (import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME === service?.unique_name) {
          currentService.value = service;
        } else if (import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME && service) {
          const brandStore = useBrandStore();
          const { domain } = useHostname();
          const subdomain = brandStore.config?.subdomains[service.unique_name];

          const anchor = document.createElement('a');
          anchor.href = `https://${subdomain}.${domain}`;
          anchor.click();
        }

        if (!skipRefetch) {
          await getUserDetails({ forceUpdate: true });
        }

        return response;
      })
      .finally(() => {
        switchWorkspacePromise.value = null;
      })
      .catch((e) => {
        throw new Error(e);
      });

    return switchWorkspacePromise.value;
  };

  /**
   * Fetches or retrieves cached details for a specific service by its unique name
   * Prevents duplicate requests by reusing active fetch promises
   *
   * @param {ServiceUniqueName} uniqueName - The unique name of the service to get.
   * @param {boolean} [forceUpdate=false] - If true, forces a refetch from the API.
   * @returns {Promise<ServiceDetails | undefined>} Promise that resolves with the service details, or undefined if service not found
   */
  const getService = async (
    uniqueName?: ServiceUniqueName,
    forceUpdate: boolean = false,
  ): Promise<ServiceDetails | undefined> => {
    if (uniqueName) {
      const existingPromise = fetchServicePromise.value[uniqueName];
      if (existingPromise) {
        return existingPromise;
      }

      if (services.value[uniqueName] && !forceUpdate) {
        return Promise.resolve(services.value[uniqueName]);
      }

      const service = currentWorkspace.value?.services.find((s) => s.unique_name === uniqueName);
      if (service) {
        const newPromise = getWorkspaceService(service).finally(() => {
          fetchServicePromise.value[uniqueName] = null;
        });
        fetchServicePromise.value[uniqueName] = newPromise;
        return newPromise;
      }
    }
  };

  /**
   * Watch for call center service changes and reconnect SIP accordingly
   * Disconnects existing SIP connection, refreshes extension details, and reconnects
   */
  watch(
    () => callCenterService.value?.id,
    async () => {
      await sipClass.disconnect();

      await getExtensionDetails(true);

      if (!sipStore.options || sipStore.options.connection === false) {
        return;
      }

      await sipClass.connect();
    },
  );

  /**
   * Fetches details for the user's extension from the API
   * The result is cached to prevent unnecessary API calls on subsequent requests
   * Also fetches phone number normalization config if web phone mode is active
   *
   * @param {boolean} [forceUpdate=false] - If true, forces a refetch from the API, ignoring any cached data.
   * @returns {Promise<ExtensionDetails | undefined>} A promise that resolves with the extension details object.
   * @throws {Error} Throws an error if the API call fails
   */
  const getExtensionDetails = async (forceUpdate: boolean = false): Promise<ExtensionDetails | undefined> => {
    if (fetchExtensionDetailsPromise.value) {
      return fetchExtensionDetailsPromise.value;
    }

    if (extensionDetails.value && !forceUpdate) {
      return Promise.resolve(extensionDetails.value);
    }

    extensionDetails.value = null;
    _normalizerConfig.value = undefined;

    const isConsole = import.meta.env.VITE_IS_CONSOLE === 'true';

    if (
      !callCenterService.value ||
      !canAccessService(callCenterService.value.status) ||
      (!sipStore.options && !isConsole)
    ) {
      return Promise.resolve(undefined);
    }

    fetchExtensionDetailsPromise.value = userApi
      .getExtensionDetails()
      .then(async ({ data }) => {
        extensionDetails.value = data;

        if (data && data.number && data.current_mode === 'webPhone' && !!sipStore.options) {
          normalizerApi.config().then((config) => {
            _normalizerConfig.value = config;
          });
        } else {
          _normalizerConfig.value = undefined;
        }

        return data;
      })
      .catch((e) => {
        throw new Error(e);
      })
      .finally(() => {
        fetchExtensionDetailsPromise.value = null;
      });
    return fetchExtensionDetailsPromise.value;
  };

  /**
   * Update user Status
   *
   * @param {UserDetails['userStatus']} status - The status to update
   */
  const updateUserStatus = (status: UserDetails['userStatus']): void => {
    if (userDetails.value) {
      userDetails.value.userStatus = status;
    }
  };

  /**
   * Resets the user store to its initial state
   */
  const reset = (): void => {
    userDetails.value = null;
    fetchPromise.value = null;
    fetchServicePromise.value = {};
    switchWorkspacePromise.value = null;
    fetchExtensionDetailsPromise.value = null;
    isFetched.value = false;
    extensionDetails.value = null;
    currentWorkspace.value = undefined;
    currentService.value = undefined;
    isImpersonateUser.value = false;
    _normalizerConfig.value = undefined;
    services.value = {};
  };

  /**
   * Sets the current workspace and service based on user details
   * Switches workspace if needed and redirects to console if no service is available
   * @private
   */
  const _setCurrentWorkSpaceAndService = async (): Promise<void> => {
    currentWorkspace.value = undefined;
    currentService.value = undefined;

    const userData = userDetails.value;
    if (!userData) {
      return;
    }

    currentWorkspace.value =
      userData.workspaces.find(
        (w) => !!userData.recent_service && w.id === userData.recent_workspaces?.[userData.recent_service],
      ) || userData.workspaces[0];

    currentService.value = currentWorkspace.value?.services.find(
      (s) => s.unique_name === import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME,
    );

    if (currentService.value && !guardServiceAccess(currentService.value)) {
      return;
    }

    if (!currentWorkspace.value) {
      return;
    }

    if (
      currentWorkspace.value.id !== Object.values(userData.recent_workspaces || {})[0] &&
      currentService.value?.id !== userData.recent_service
    ) {
      const service = currentService.value || currentWorkspace.value.services[0];
      if (service) {
        await switchWorkspaceService(currentWorkspace.value, service, true);
      }
    }

    if (import.meta.env.VITE_CURRENT_SERVICE_UNIQUE_NAME) {
      if (currentService.value) {
        await getWorkspaceService(currentService.value).catch(() => goToConsole());
      } else {
        goToConsole();
      }
    }
  };

  /**
   * Clears all cached services and extension details
   * @private
   */
  const _resetServices = async (): Promise<void> => {
    services.value = {} as Record<ServiceUniqueName, ServiceDetails>;
    extensionDetails.value = null;
  };

  /**
   * Initializes impersonation session if an impersonation token exists
   * Sets up automatic logout when the impersonation token expires
   * @private
   */
  const _startImpersonationSession = (): void => {
    const impersonateTokenExpiresAt = useAuthStore().impersonateTokenExpiresAt;
    isImpersonateUser.value = !!impersonateTokenExpiresAt;

    if (isImpersonateUser.value) {
      impersonateWidget.initWidget();
      if (impersonateTokenExpiresAt) {
        // Logout after expiration
        setTimeout(
          () => {
            useAuthStore().endImpersonationSession();
          },
          Number.parseInt(impersonateTokenExpiresAt) - moment(moment.now()).valueOf(),
        );
      }
    }
  };

  return {
    // Indicates whether user details have been fetched
    isFetched: readonly(isFetched),
    // Indicates whether user details are currently being fetched
    isFetching,
    // Current authenticated user's details
    userDetails,
    // Current authenticated user's permissions
    permissions: readonly(permissions),
    // Current user's extension details for call center functionality
    extensionDetails,
    // Cached service details, keyed by service unique name
    services,
    // User's currently active workspace
    currentWorkspace,
    // User's currently active service
    currentService,
    // Indicates whether the current session is an impersonation session
    isImpersonateUser,

    // Current service details from the services cache
    currentServiceDetails,
    // Call center service details from the services cache
    callCenterService,
    // Cloud lines service details from the services cache
    cloudLinesService,
    // New business chat service details from the services cache
    newChatService,
    // Phone number normalization configuration
    normalizerConfig,

    // Checkers for module availability and roles
    isOwner,
    callCenterSettings,

    // Fetches and initializes the authenticated user's details from the API
    getUserDetails,
    // Fetches details for all services in the current workspace
    getAllServices,
    // Checks if the current user has specific permissions
    hasPermissions,
    // Fetches details for a specific service within the current workspace
    getWorkspaceService,
    // Switches the user's active workspace and/or service
    switchWorkspaceService,
    // Fetches or retrieves cached details for a specific service
    getService,
    // Fetches details for the user's extension from the API
    getExtensionDetails,
    // Resets the user store to its initial state
    reset,
    // Update user details
    updateUserStatus,
  };
});
